/**
 * School Management API Service
 */

import { api } from './api';
import type {
  SchoolYear,
  Trimester,
  ClassRoom,
  Subject,
  TimetableSlot,
  Homework,
  HomeworkSubmission,
  AttendanceStats,
  BulletinNote,
  Bulletin,
  RemarkDraft,
  SetupPreset,
  Competency,
  CompetencyEvaluation,
  LivretScolaire,
  SuiviPedagogique,
  Appointment,
  CalendarEvent,
  TeacherClassroom,
  GradeSheet,
  ParentReportCard,
  ReportCardSignature,
  GradeHistoryItem,
} from '@/types/school-management';

export interface ClassroomSubjectRow {
  id: string
  subject_id: string
  name: string
  code: string | null
  teacher_id: string | null
  coefficient: number | null
  weekly_hours: number | null
}

const BASE = '/school-management';

export const schoolManagementService = {
  // ─── School Members ──────────────────────────────
  async getSchoolMembers(role?: string): Promise<{ user_id: string; name: string; email: string; role: string; student_id?: string }[]> {
    const params = role ? { role } : {};
    const { data } = await api.get(`${BASE}/school-members`, { params });
    return data;
  },
  async getStudentsForEnrollment(): Promise<{ student_id: string; name: string; email: string; matricule: string }[]> {
    const { data } = await api.get(`${BASE}/students-for-enrollment`);
    return data;
  },

  // ─── School Year & Trimester ─────────────────────
  async getSchoolYears(): Promise<SchoolYear[]> {
    const { data } = await api.get(`${BASE}/school-years`);
    return data;
  },
  async createSchoolYear(payload: Partial<SchoolYear>): Promise<SchoolYear> {
    const { data } = await api.post(`${BASE}/school-years`, payload);
    return data;
  },
  async createTrimester(payload: Partial<Trimester> & { school_year_id: string }): Promise<Trimester> {
    const { data } = await api.post(`${BASE}/trimesters`, payload);
    return data;
  },
  /** The year's three usual terms, dated on the school calendar and named in `lang`. */
  async createDefaultTrimesters(yearId: string, lang?: string): Promise<Trimester[]> {
    const { data } = await api.post(`${BASE}/school-years/${yearId}/default-trimesters`, null, { params: lang ? { lang } : {} });
    return data;
  },

  // ─── Classrooms ──────────────────────────────────
  async getClassrooms(schoolYearId?: string): Promise<ClassRoom[]> {
    const params = schoolYearId ? { school_year_id: schoolYearId } : {};
    const { data } = await api.get(`${BASE}/classrooms`, { params });
    return data;
  },
  /** Ready-made classes and subjects per cycle (primaire, collège, Form 1-5...). */
  async getSetupPresets(): Promise<SetupPreset[]> {
    const { data } = await api.get(`${BASE}/setup-presets`);
    return data;
  },
  async applySetupPresets(cycles: { key: string; classes?: string[]; skip_subjects?: string[] }[]): Promise<{ classes: number; subjects: number; links: number; skipped_classes: number }> {
    const { data } = await api.post(`${BASE}/setup-presets/apply`, { cycles });
    return data;
  },
  /** Another section of the same level: same subjects and coefficients, no pupils or teachers. */
  async cloneClassroom(classroomId: string, name: string, section?: string): Promise<ClassRoom> {
    const { data } = await api.post(`${BASE}/classrooms/${classroomId}/clone`, { name, section });
    return data;
  },
  async createClassroom(payload: Partial<ClassRoom>): Promise<ClassRoom> {
    const { data } = await api.post(`${BASE}/classrooms`, payload);
    return data;
  },
  async getClassroomStudents(classroomId: string): Promise<{ student_id: string; name: string }[]> {
    const { data } = await api.get(`${BASE}/classrooms/${classroomId}/students`);
    return data;
  },
  async addStudentToClassroom(classroomId: string, studentId: string) {
    return api.post(`${BASE}/classrooms/${classroomId}/students`, { student_id: studentId });
  },
  /** Put the pupil in this class only (leaves their other classes of the year). */
  async placeStudentInClassroom(classroomId: string, studentId: string) {
    return api.post(`${BASE}/classrooms/${classroomId}/students`, { student_id: studentId }, { params: { replace: true } });
  },
  async removeStudentFromClassroom(classroomId: string, studentId: string) {
    return api.delete(`${BASE}/classrooms/${classroomId}/students/${studentId}`);
  },
  async moveStudentBetweenClassrooms(studentId: string, fromClassroomId: string, toClassroomId: string) {
    return api.post(`${BASE}/classrooms/move-student`, { student_id: studentId, from_classroom_id: fromClassroomId, to_classroom_id: toClassroomId });
  },
  async addSubjectToClassroom(classroomId: string, payload: { subject_id: string; teacher_id?: string; coefficient?: number; weekly_hours?: number }) {
    return api.post(`${BASE}/classrooms/${classroomId}/subjects`, payload);
  },
  async getClassroomSubjects(classroomId: string): Promise<ClassroomSubjectRow[]> {
    const { data } = await api.get(`${BASE}/classrooms/${classroomId}/subjects`);
    return data;
  },
  /** A subject's coefficient or weekly hours in one class. */
  async updateClassroomSubject(classroomId: string, subjectId: string, patch: { coefficient?: number; weekly_hours?: number }) {
    const { data } = await api.patch(`${BASE}/classrooms/${classroomId}/subjects/${subjectId}`, patch);
    return data;
  },

  // ─── Subjects ────────────────────────────────────
  async getSubjects(): Promise<Subject[]> {
    const { data } = await api.get(`${BASE}/subjects`);
    return data;
  },
  async createSubject(payload: Partial<Subject>): Promise<Subject> {
    const { data } = await api.post(`${BASE}/subjects`, payload);
    return data;
  },

  // ─── Timetable ───────────────────────────────────
  async getTimetable(classroomId: string): Promise<TimetableSlot[]> {
    const { data } = await api.get(`${BASE}/timetable/${classroomId}`);
    return data;
  },
  async getMySchedule(): Promise<(TimetableSlot & { classroom_name?: string })[]> {
    const { data } = await api.get(`${BASE}/timetable/my-schedule`);
    return data;
  },
  async createTimetableSlot(payload: Partial<TimetableSlot>): Promise<TimetableSlot> {
    const { data } = await api.post(`${BASE}/timetable`, payload);
    return data;
  },
  async updateTimetableSlot(slotId: string, payload: Partial<TimetableSlot>): Promise<TimetableSlot> {
    const { data } = await api.put(`${BASE}/timetable/${slotId}`, payload);
    return data;
  },
  async deleteTimetableSlot(slotId: string): Promise<void> {
    await api.delete(`${BASE}/timetable/${slotId}`);
  },

  // ─── Homework ────────────────────────────────────
  async getHomework(classroomId?: string, subjectId?: string): Promise<Homework[]> {
    const params: Record<string, string> = {};
    if (classroomId) params.classroom_id = classroomId;
    if (subjectId) params.subject_id = subjectId;
    const { data } = await api.get(`${BASE}/homework`, { params });
    return data;
  },
  async createHomework(payload: Partial<Homework>): Promise<Homework> {
    const { data } = await api.post(`${BASE}/homework`, payload);
    return data;
  },
  async updateHomework(homeworkId: string, payload: Partial<Pick<Homework, 'title' | 'description' | 'instructions' | 'due_date' | 'max_score'>>): Promise<Homework> {
    const { data } = await api.put(`${BASE}/homework/${homeworkId}`, payload);
    return data;
  },
  async deleteHomework(homeworkId: string): Promise<void> {
    await api.delete(`${BASE}/homework/${homeworkId}`);
  },
  async getHomeworkSubmissions(homeworkId: string): Promise<HomeworkSubmission[]> {
    const { data } = await api.get(`${BASE}/homework/${homeworkId}/submissions`);
    return data;
  },
  async submitHomework(homeworkId: string, studentId: string, attachments?: string[]): Promise<HomeworkSubmission> {
    const { data } = await api.post(`${BASE}/homework/${homeworkId}/submit`, { homework_id: homeworkId, student_id: studentId, attachments });
    return data;
  },
  async gradeSubmission(submissionId: string, score: number, feedback?: string): Promise<HomeworkSubmission> {
    const { data } = await api.put(`${BASE}/homework/submissions/${submissionId}/grade`, { score, feedback });
    return data;
  },

  // ─── Attendance ──────────────────────────────────
  async getAttendanceByDate(classroomId: string, date: string): Promise<{ student_id: string; status: string; notes?: string; excused_reason?: string }[]> {
    const { data } = await api.get(`${BASE}/attendance/${classroomId}/date/${date}`);
    return data;
  },
  async recordAttendanceBulk(classroomId: string, date: string, records: { student_id: string; status: string; notes?: string }[]) {
    const { data } = await api.post(`${BASE}/attendance/bulk`, { classroom_id: classroomId, date, records });
    return data;
  },
  async getAttendanceStats(classroomId: string, startDate?: string, endDate?: string): Promise<AttendanceStats[]> {
    const params: Record<string, string> = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const { data } = await api.get(`${BASE}/attendance/stats/${classroomId}`, { params });
    return data;
  },

  // ─── Bulletin (Secondary) ────────────────────────
  async createBulletinNote(payload: Partial<BulletinNote> & { classroom_id: string; trimester_id: string }): Promise<BulletinNote> {
    const { data } = await api.post(`${BASE}/bulletin-notes`, payload);
    return data;
  },
  async generateBulletins(classroomId: string, trimesterId: string, observation?: string): Promise<Bulletin[]> {
    const { data } = await api.post(`${BASE}/bulletins/generate`, { classroom_id: classroomId, trimester_id: trimesterId, observation_conseil: observation });
    return data;
  },
  /** AI drafts of the general remark for every report card of the class; nothing is saved. */
  async draftBulletinRemarks(classroomId: string, trimesterId: string, language?: string): Promise<RemarkDraft[]> {
    const { data } = await api.post(`${BASE}/bulletins/remarks/draft`, { classroom_id: classroomId, trimester_id: trimesterId, language });
    return data;
  },
  async saveBulletinRemarks(classroomId: string, remarks: { bulletin_id: string; observation_conseil: string }[]): Promise<{ saved: number }> {
    const { data } = await api.put(`${BASE}/bulletins/remarks`, { classroom_id: classroomId, remarks });
    return data;
  },
  async getStudentBulletins(studentId: string): Promise<Bulletin[]> {
    const { data } = await api.get(`${BASE}/bulletins/${studentId}`);
    return data;
  },
  async publishBulletin(bulletinId: string) {
    return api.put(`${BASE}/bulletins/${bulletinId}/publish`);
  },

  async publishLivret(livretId: string) {
    return api.put(`${BASE}/livrets/${livretId}/publish`);
  },

  async downloadBulletinPdf(bulletinId: string): Promise<void> {
    const response = await api.get(`${BASE}/bulletins/${bulletinId}/pdf`, { responseType: 'blob' });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bulletin_${bulletinId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  async downloadLivretPdf(livretId: string): Promise<void> {
    const response = await api.get(`${BASE}/livrets/${livretId}/pdf`, { responseType: 'blob' });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `livret_${livretId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  // ─── Competency & Livret (Primary) ───────────────
  async getCompetencies(domain?: string): Promise<Competency[]> {
    const params = domain ? { domain } : {};
    const { data } = await api.get(`${BASE}/competencies`, { params });
    return data;
  },
  async createCompetency(payload: Partial<Competency>): Promise<Competency> {
    const { data } = await api.post(`${BASE}/competencies`, payload);
    return data;
  },
  async createCompetencyEvaluation(payload: Partial<CompetencyEvaluation> & { classroom_id: string; trimester_id: string }): Promise<CompetencyEvaluation> {
    const { data } = await api.post(`${BASE}/competency-evaluations`, payload);
    return data;
  },
  async getCompetencyEvaluations(classroomId: string, trimesterId: string): Promise<CompetencyEvaluation[]> {
    const { data } = await api.get(`${BASE}/competency-evaluations`, { params: { classroom_id: classroomId, trimester_id: trimesterId } });
    return data;
  },
  async generateLivrets(classroomId: string, trimesterId: string): Promise<LivretScolaire[]> {
    const { data } = await api.post(`${BASE}/livrets/generate`, { classroom_id: classroomId, trimester_id: trimesterId });
    return data;
  },
  async getStudentLivrets(studentId: string): Promise<LivretScolaire[]> {
    const { data } = await api.get(`${BASE}/livrets/${studentId}`);
    return data;
  },

  // ─── Suivi Pédagogique ───────────────────────────
  async createSuivi(payload: Partial<SuiviPedagogique>): Promise<SuiviPedagogique> {
    const { data } = await api.post(`${BASE}/suivi`, payload);
    return data;
  },
  async getTeacherSuivis(observationType?: string): Promise<SuiviPedagogique[]> {
    const params = observationType ? { observation_type: observationType } : {};
    const { data } = await api.get(`${BASE}/suivi`, { params });
    return data;
  },
  async getStudentSuivi(studentId: string, observationType?: string): Promise<SuiviPedagogique[]> {
    const params = observationType ? { observation_type: observationType } : {};
    const { data } = await api.get(`${BASE}/suivi/${studentId}`, { params });
    return data;
  },
  async updateSuivi(id: string, payload: Partial<SuiviPedagogique>): Promise<SuiviPedagogique> {
    const { data } = await api.put(`${BASE}/suivi/${id}`, payload);
    return data;
  },
  async deleteSuivi(id: string): Promise<void> {
    await api.delete(`${BASE}/suivi/${id}`);
  },
  async getSuiviDashboard(): Promise<any> {
    const { data } = await api.get(`${BASE}/suivi-dashboard`);
    return data;
  },
  async getAtRiskStudents(): Promise<any[]> {
    const { data } = await api.get(`${BASE}/suivi-at-risk`);
    return data;
  },
  async getStudentTimeline(studentId: string): Promise<any> {
    const { data } = await api.get(`${BASE}/suivi-timeline/${studentId}`);
    return data;
  },
  async exportSuiviPDF(studentId?: string): Promise<void> {
    const params = studentId ? { student_id: studentId } : {};
    const response = await api.get(`${BASE}/suivi-export-pdf`, { params, responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = `suivi-pedagogique${studentId ? `-${studentId}` : ''}.pdf`;
    a.click();
    window.URL.revokeObjectURL(url);
  },

  async getSuiviAIAnalysis(studentId: string, language?: string): Promise<{
    student_id: string;
    student_name: string;
    student_gender: string;
    observation_count: number;
    data_richness: {
      observations: number;
      grades: number;
      attendance_days: number;
      homework_submissions: number;
      peer_conflicts: number;
    };
    computed_metrics: {
      risk_score: number;
      risk_level: string;
      risk_factors: { factor: string; score: number; max: number; detail: string }[];
      observation_trend: string;
      grade_trajectory: string;
      attendance_rate: number | null;
      homework_completion: number | null;
      correlations: Record<string, { detected: boolean; description: string; strength?: string; affected_subjects?: string[] }>;
      temporal_patterns: Record<string, unknown>;
    };
    peer_conflicts: {
      peer_name: string;
      peer_student_id: string;
      shared_incident_dates: string[];
      peer_behavioral_count: number;
      name_cross_referenced: boolean;
      cross_ref_direction: string;
      conflict_severity: string;
      relevant_observations: { date: string; type: string; severity: string; observation: string; resolution: string }[];
    }[];
    analysis: {
      patterns: string[];
      risk_level: string;
      risk_score: number;
      predictions: string[];
      conflict_analysis: string[];
      suggestions: string[];
      parent_guidance: string[];
      teacher_guidance: string[];
      immediate_actions: string[];
      summary: string;
      source?: string;
    };
  }> {
    const params = language ? { language } : {};
    const { data } = await api.get(`${BASE}/suivi/${studentId}/ai-analysis`, { params });
    return data;
  },

  // ─── Appointments ────────────────────────────────
  async getAppointments(status?: string): Promise<Appointment[]> {
    const params = status ? { status } : {};
    const { data } = await api.get(`${BASE}/appointments`, { params });
    return data;
  },
  async createAppointment(payload: Partial<Appointment>): Promise<Appointment> {
    const { data } = await api.post(`${BASE}/appointments`, payload);
    return data;
  },
  async updateAppointment(id: string, payload: Partial<Appointment>): Promise<Appointment> {
    const { data } = await api.put(`${BASE}/appointments/${id}`, payload);
    return data;
  },

  // ─── Calendar ────────────────────────────────────
  async getCalendarEvents(startDate?: string, endDate?: string, eventType?: string): Promise<CalendarEvent[]> {
    const params: Record<string, string> = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (eventType) params.event_type = eventType;
    const { data } = await api.get(`${BASE}/calendar`, { params });
    return data;
  },
  async createCalendarEvent(payload: Partial<CalendarEvent>): Promise<CalendarEvent> {
    const { data } = await api.post(`${BASE}/calendar`, payload);
    return data;
  },

  // ─── Teacher Grade Entry ─────────────────────────
  async getTeacherClassrooms(): Promise<TeacherClassroom[]> {
    const { data } = await api.get(`${BASE}/teacher/my-classes`);
    return data;
  },
  async getGradeSheet(classroomId: string, subjectId: string, trimesterId: string, sequenceNumber?: number): Promise<GradeSheet> {
    const params: Record<string, string> = { trimester_id: trimesterId };
    if (sequenceNumber) params.sequence_number = String(sequenceNumber);
    const { data } = await api.get(`${BASE}/teacher/grade-sheet/${classroomId}/${subjectId}`, { params });
    return data;
  },
  async bulkCreateGrades(payload: {
    classroom_id: string;
    subject_id: string;
    trimester_id: string;
    coefficient: number;
    sequence_number?: number;
    entries: { student_id: string; note: number; appreciation?: string }[];
  }): Promise<BulletinNote[]> {
    const { data } = await api.post(`${BASE}/teacher/grades/bulk`, payload);
    return data;
  },
  async syncOfflineGrades(grades: {
    student_id: string; classroom_id: string; subject_id: string; trimester_id: string;
    note: number; coefficient: number; sequence_number?: number; appreciation?: string; local_id?: string;
  }[]): Promise<{ synced: number; failed: number; results: { local_id?: string; server_id?: string; status: string; error?: string }[] }> {
    const { data } = await api.post(`${BASE}/teacher/grades/sync`, { grades });
    return data;
  },
  async getGradeHistory(filters?: { classroom_id?: string; subject_id?: string; trimester_id?: string; student_name?: string }): Promise<GradeHistoryItem[]> {
    const { data } = await api.get(`${BASE}/teacher/grades/history`, { params: filters });
    return data;
  },

  // ─── Parent Report Cards ─────────────────────────
  async getParentReportCards(studentId: string): Promise<ParentReportCard[]> {
    const { data } = await api.get(`${BASE}/parent/report-cards/${studentId}`);
    return data;
  },
  async signReportCard(payload: { bulletin_id?: string; livret_id?: string; comment?: string }): Promise<ReportCardSignature> {
    const { data } = await api.post(`${BASE}/parent/sign-report-card`, payload);
    return data;
  },
  async getReportCardSignatures(reportId: string, reportType: string = 'bulletin'): Promise<ReportCardSignature[]> {
    const { data } = await api.get(`${BASE}/report-card/signatures/${reportId}`, { params: { report_type: reportType } });
    return data;
  },

  // ─── School Statistics ──────────────────────────────────────────────────
  async getSchoolStats(schoolId: string, params?: { school_year_id?: string; trimester_id?: string }) {
    const { data } = await api.get(`${BASE}/school-stats/${schoolId}`, { params });
    return data;
  },
  async getClassReport(classroomId: string, params?: { trimester_id?: string }) {
    const { data } = await api.get(`${BASE}/class-report/${classroomId}`, { params });
    return data;
  },
  async exportSchoolStatsCSV(schoolId: string, params?: { trimester_id?: string }) {
    const response = await api.get(`${BASE}/school-stats/${schoolId}/export-csv`, {
      params,
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const disposition = response.headers['content-disposition'];
    const filename = disposition?.match(/filename="(.+)"/)?.[1] || 'school_stats.csv';
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  async getSubjectStats(schoolId: string, params?: { trimester_id?: string }) {
    const { data } = await api.get(`${BASE}/school-stats/${schoolId}/subjects`, { params });
    return data;
  },
  async exportProcesVerbal(schoolId: string, trimesterId: string) {
    const response = await api.get(`${BASE}/school-stats/${schoolId}/procès-verbal`, {
      params: { trimester_id: trimesterId },
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const disposition = response.headers['content-disposition'];
    const filename = disposition?.match(/filename="(.+)"/)?.[1] || 'proces_verbal.csv';
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  async exportSchoolStatsPDF(schoolId: string, params?: { trimester_id?: string }) {
    const response = await api.get(`${BASE}/school-stats/${schoolId}/export-pdf`, {
      params,
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const disposition = response.headers['content-disposition'];
    const filename = disposition?.match(/filename="(.+)"/)?.[1] || 'school_stats.pdf';
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  async compileAttendance(classroomId: string, trimesterId: string) {
    const { data } = await api.post(`${BASE}/compile-attendance/${classroomId}`, null, { params: { trimester_id: trimesterId } });
    return data;
  },

  // ─── Leave Requests ─────────────────────────────────────────────────────
  async createLeaveRequest(payload: { student_id: string; school_id?: string; leave_type: string; reason: string; start_date: string; end_date: string; document_url?: string }) {
    const { data } = await api.post(`${BASE}/leave-requests`, payload);
    return data;
  },
  async listLeaveRequests(schoolId: string, params?: { status?: string; classroom_id?: string }) {
    const { data } = await api.get(`${BASE}/leave-requests`, { params: { school_id: schoolId, ...params } });
    return data;
  },
  async reviewLeaveRequest(requestId: string, payload: { status: string; review_comment?: string }) {
    const { data } = await api.put(`${BASE}/leave-requests/${requestId}/review`, payload);
    return data;
  },
  async getParentLeaveRequests(studentId?: string) {
    const { data } = await api.get(`${BASE}/parent/leave-requests`, { params: studentId ? { student_id: studentId } : {} });
    return data;
  },

  // ─── Staff Leave Requests ─────────────────────────────────────────────────
  async getStaffLeaveRequests(status?: string): Promise<any[]> {
    const params = status ? { status } : {};
    const { data } = await api.get(`${BASE}/staff-leave-requests`, { params });
    return Array.isArray(data) ? data : data.items || [];
  },
  async createStaffLeaveRequest(payload: {
    user_id?: string;
    leave_type: string;
    reason: string;
    start_date: string;
    end_date: string;
    document_url?: string;
  }): Promise<any> {
    const { data } = await api.post(`${BASE}/staff-leave-requests`, payload);
    return data;
  },
  async reviewStaffLeaveRequest(requestId: string, status: string, comment?: string): Promise<any> {
    const { data } = await api.put(`${BASE}/staff-leave-requests/${requestId}/review`, {
      status,
      review_comment: comment,
    });
    return data;
  },

  // ─── Invitations ─────────────────────────────────
  async createInvitation(payload: { email?: string; role: string; max_uses?: number; expires_at?: string }): Promise<any> {
    const { data } = await api.post('/schools/invitations', payload);
    return data;
  },
  /** The school's invitations, newest first, each with a status the list can
   * filter on: accepted (fully used), expired, revoked, or pending. */
  async getInvitations(): Promise<any[]> {
    const { data } = await api.get('/schools/invitations');
    const items: any[] = Array.isArray(data) ? data : (data?.invitations ?? data?.items ?? []);
    const now = Date.now();
    return items.map((inv) => ({
      ...inv,
      status: inv.status ?? (
        !inv.is_active ? 'revoked'
          : (inv.current_uses ?? 0) >= (inv.max_uses ?? 1) ? 'accepted'
          : inv.expires_at && new Date(inv.expires_at.endsWith('Z') ? inv.expires_at : `${inv.expires_at}Z`).getTime() < now ? 'expired'
          : 'pending'
      ),
    })).sort((a, b) => String(b.created_at ?? '').localeCompare(String(a.created_at ?? '')));
  },

  // ─── Email Configuration ─────────────────────────
  async getSchoolEmailConfig(): Promise<{
    email_from_name: string | null
    email_reply_to: string | null
    email_signature: string | null
    platform_from_email: string
    example_from: string
  }> {
    const { data } = await api.get('/schools/email-config');
    return data;
  },
  async updateSchoolEmailConfig(payload: {
    email_from_name?: string | null
    email_reply_to?: string | null
    email_signature?: string | null
  }) {
    const { data } = await api.put('/schools/email-config', payload);
    return data;
  },

  // ─── Report Card Templates ──────────────────────────────────────────────
  async uploadReportCardTemplate(file: File, name: string, templateType: string = 'bulletin') {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await api.post(
      `${BASE}/report-card-templates/upload?name=${encodeURIComponent(name)}&template_type=${templateType}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return data;
  },
  async getReportCardTemplates(templateType?: string) {
    const params = templateType ? { template_type: templateType } : {};
    const { data } = await api.get(`${BASE}/report-card-templates`, { params });
    return data;
  },
  async activateReportCardTemplate(templateId: string) {
    const { data } = await api.put(`${BASE}/report-card-templates/${templateId}/activate`);
    return data;
  },
  async getReportCardTemplatePreview(templateId: string) {
    const { data } = await api.get(`${BASE}/report-card-templates/${templateId}/preview`);
    return data;
  },
  async deleteReportCardTemplate(templateId: string) {
    const { data } = await api.delete(`${BASE}/report-card-templates/${templateId}`);
    return data;
  },

  async validateReportCardTemplate(templateId: string) {
    const { data } = await api.post(`${BASE}/report-card-templates/${templateId}/validate`);
    return data;
  },
};
